import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import { BRAND } from '../../config/brand';

interface BrandHeroProps {
  title?: string;
  subtitle?: string;
  description?: string;
  backgroundImage?: string;
  overlay?: 'navy' | 'light';
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  minHeight?: number | { xs: number; md: number };
  align?: 'center' | 'left';
}

export const BrandHero: React.FC<BrandHeroProps> = ({
  title = `${BRAND.name} ${BRAND.subtitle}`,
  subtitle = BRAND.tagline,
  description,
  backgroundImage = BRAND.images.facilityExterior,
  overlay = 'navy',
  primaryAction,
  secondaryAction,
  minHeight = { xs: 420, md: 560 },
  align = 'center',
}) => {
  const isCenter = align === 'center';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: BRAND.colors.navyDark,
      }}
    >
      <Box
        component="img"
        src={backgroundImage}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            overlay === 'navy'
              ? `linear-gradient(135deg, rgba(15,52,96,0.92) 0%, rgba(27,77,137,0.78) 55%, rgba(15,52,96,0.88) 100%)`
              : `linear-gradient(135deg, rgba(242,233,220,0.92) 0%, rgba(250,246,240,0.85) 100%)`,
        }}
      />
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 8, md: 10 },
          textAlign: isCenter ? 'center' : 'left',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: overlay === 'navy' ? BRAND.colors.cream : BRAND.colors.navyLight,
            letterSpacing: '0.2em',
            fontWeight: 700,
            mb: 1,
            display: 'block',
          }}
        >
          {subtitle}
        </Typography>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            color: overlay === 'navy' ? BRAND.colors.white : BRAND.colors.navy,
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '3.25rem' },
            lineHeight: 1.15,
            mb: 2,
            maxWidth: isCenter ? 900 : 700,
            mx: isCenter ? 'auto' : 0,
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            variant="h6"
            sx={{
              color: overlay === 'navy' ? 'rgba(255,255,255,0.9)' : 'text.secondary',
              fontWeight: 400,
              maxWidth: 760,
              mx: isCenter ? 'auto' : 0,
              mb: 4,
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            {description}
          </Typography>
        )}
        {(primaryAction || secondaryAction) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent={isCenter ? 'center' : 'flex-start'}
            sx={{ mt: 2 }}
          >
            {primaryAction && (
              <Button
                variant="contained"
                size="large"
                onClick={primaryAction.onClick}
                sx={{
                  px: 4,
                  py: 1.5,
                  bgcolor: BRAND.colors.cream,
                  color: BRAND.colors.navy,
                  fontWeight: 700,
                  '&:hover': { bgcolor: BRAND.colors.white },
                }}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="outlined"
                size="large"
                onClick={secondaryAction.onClick}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: BRAND.colors.cream,
                  color: BRAND.colors.cream,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: BRAND.colors.white,
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                {secondaryAction.label}
              </Button>
            )}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default BrandHero;
